import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Save, Loader2, CreditCard } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { getMyPayoutProfile, upsertMyPayoutProfile } from "@/lib/wallet.functions";

export const Route = createFileRoute("/settings/payouts")({
  head: () => ({ meta: [{ title: "Método de pago — ZYNTRA" }] }),
  component: PayoutsSettings,
});

async function withAuth<T>(fn: (h: any) => Promise<T>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return fn({ authorization: token ? `Bearer ${token}` : "" });
}

function PayoutsSettings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [form, setForm] = useState({
    full_name: "",
    method: "sinpe" as "bank_transfer" | "sinpe" | "paypal" | "other",
    document_id: "",
    bank_name: "",
    account_number: "",
    iban: "",
    sinpe_number: "",
    email_for_payment: "",
    notes: "",
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    (async () => {
      try {
        await withAuth(async (headers) => {
          const { profile } = await getMyPayoutProfile({ headers } as any);
          if (profile) {
            setForm({
              full_name: profile.full_name || "",
              method: (profile.method as any) || "sinpe",
              document_id: profile.document_id || "",
              bank_name: profile.bank_name || "",
              account_number: profile.account_number || "",
              iban: profile.iban || "",
              sinpe_number: profile.sinpe_number || "",
              email_for_payment: profile.email_for_payment || "",
              notes: profile.notes || "",
            });
          }
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading]);

  async function save() {
    setMsg(null);
    if (!form.full_name.trim()) {
      setMsg({ type: "err", text: "El nombre completo es requerido." }); return;
    }
    setSaving(true);
    try {
      await withAuth((headers) => upsertMyPayoutProfile({
        data: { ...form, email_for_payment: form.email_for_payment || null } as any,
        headers,
      } as any));
      setMsg({ type: "ok", text: "Método de pago guardado." });
    } catch (e: any) {
      setMsg({ type: "err", text: e.message });
    }
    setSaving(false);
  }

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-2xl px-4 pb-20 pt-24 sm:px-6">
      <Link to="/wallet" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Volver al wallet
      </Link>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-3 mb-1">
          <CreditCard className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-bold text-foreground">Método de pago</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Configurá cómo querés recibir tus retiros. Esta información solo es visible para vos y los administradores.
        </p>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nombre completo *" full>
            <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
          </Field>

          <Field label="Método *">
            <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value as any })} className={inputCls}>
              <option value="sinpe">SINPE Móvil</option>
              <option value="bank_transfer">Transferencia bancaria</option>
              <option value="paypal">PayPal</option>
              <option value="other">Otro</option>
            </select>
          </Field>

          <Field label="Documento de identidad">
            <input type="text" value={form.document_id} onChange={(e) => setForm({ ...form, document_id: e.target.value })} className={inputCls} />
          </Field>

          {form.method === "sinpe" && (
            <Field label="Número SINPE *" full>
              <input type="tel" value={form.sinpe_number} onChange={(e) => setForm({ ...form, sinpe_number: e.target.value })} className={inputCls} />
            </Field>
          )}

          {form.method === "bank_transfer" && (
            <>
              <Field label="Banco">
                <input type="text" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Número de cuenta">
                <input type="text" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className={inputCls} />
              </Field>
              <Field label="IBAN" full>
                <input type="text" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className={inputCls} />
              </Field>
            </>
          )}

          {form.method === "paypal" && (
            <Field label="Email PayPal *" full>
              <input type="email" value={form.email_for_payment} onChange={(e) => setForm({ ...form, email_for_payment: e.target.value })} className={inputCls} />
            </Field>
          )}

          {form.method === "other" && (
            <Field label="Email de contacto" full>
              <input type="email" value={form.email_for_payment} onChange={(e) => setForm({ ...form, email_for_payment: e.target.value })} className={inputCls} />
            </Field>
          )}

          <Field label="Notas / instrucciones extra" full>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
          </Field>
        </div>

        {msg && (
          <p className={`mt-4 rounded-lg px-3 py-2 text-xs ${msg.type === "ok" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
            {msg.text}
          </p>
        )}

        <button
          onClick={save}
          disabled={saving}
          className="mt-5 inline-flex items-center gap-2 rounded-xl gradient-primary px-5 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Guardar
        </button>
      </div>
    </div>
  );
}

const inputCls = "mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none";

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-xs font-semibold text-foreground">{label}</label>
      {children}
    </div>
  );
}
